<script lang="ts" setup>
import Datepicker, { DatePickerInstance } from '@vuepic/vue-datepicker'
import { ref, watch } from 'vue'
import dayjs, { ManipulateType } from 'dayjs'
import { useI18n } from 'vue-i18n'

import { usePresetRangeList } from '@/hooks/use-preset-range-list'
import { MyDateRangePickerRef } from '@/types/general'

interface Props {
    // Most tables use filters with these values. When provided it'll use those as default
    filter?: {
        'start-date': string | undefined
        'end-date': string | undefined
    }
    // When true the height of the date range will match MyInput's height
    useInputHeight?: boolean
    // When true it'll be rendered as an input field instead of button
    asInput?: boolean
    disableAllTimeOption?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    useInputHeight: false,
    asInput: false,
    disableAllTimeOption: false,
})
const date = defineModel<Date[]>({
    required: false,
    default: (p: Props) => {
        if (!p.filter || !p.filter['start-date'] || !p.filter['end-date']) {
            return []
        }

        return [dayjs(p.filter['start-date']).toDate(), dayjs(p.filter['end-date']).toDate()]
    },
})

const emit = defineEmits<{
    (e: 'startDateChanged', date: string | undefined): void
    (e: 'endDateChanged', date: string | undefined): void
}>()

const presetRanges = usePresetRangeList()
const { t } = useI18n()

const datepicker = ref<DatePickerInstance>(null)

function clearDates() {
    datepicker.value?.closeMenu()
    date.value = []
}

function increaseRange(days: number, unit: ManipulateType = 'days') {
    datepicker.value?.closeMenu()
    date.value = [
        dayjs().subtract(days, unit).startOf('day').toDate(),
        dayjs().endOf('day').toDate(),
    ]
}

watch(date, () => {
    let startDate: string | undefined = undefined
    let endDate: string | undefined = undefined
    if (date.value[0]) {
        startDate = dayjs(date.value[0]).startOf('day').toISOString()
    }
    if (date.value[1]) {
        endDate = dayjs(date.value[1]).endOf('day').toISOString()
    }

    emit('startDateChanged', startDate)
    emit('endDateChanged', endDate)
})

defineExpose<MyDateRangePickerRef>({ increaseRange, clearDates })
</script>

<template>
    <Datepicker
        ref="datepicker"
        v-model="date"
        :ui="{ input: props.useInputHeight ? 'w-64! h-10' : 'w-64!' }"
        :class="props.asInput ? 'input-field w-full' : ''"
        range
        :enable-time-picker="false"
        auto-apply
        :teleport="true"
        :clearable="props.asInput"
        :preset-dates="presetRanges"
        close-on-scroll
        position="left"
        format="yyyy-MM-dd"
        :transitions="false"
        :placeholder="t('allTime')"
        :config="{ closeOnAutoApply: true }"
        @cleared="date = []"
    >
        <template v-if="!disableAllTimeOption" #allTime>
            <button class="dp__btn dp--preset-range" @click="clearDates" v-text="t('allTime')" />
        </template>
    </Datepicker>
</template>
